/**
 * One issued certificate — the page a learner shares.
 *
 * Structure is the prototype's `screen-certificate`: a head, the certificate card (seal,
 * eyebrow, title, recipient, signature footer), then the actions. Class names are the
 * prototype's too, so the two can be put side by side.
 *
 * This replaces the platform's own server-rendered `/certificates/<uuid>` page. That page
 * works, and it is somebody else's design: an Open edX layout with its own header, its own
 * footer and its own copy about validating certificates. On a deployment whose entire point
 * is that the academy owns every surface, the one page a learner actually shows to other
 * people was the one page that did not look like the academy.
 *
 * TWO KINDS OF CREDENTIAL, ONE DOCUMENT
 * -------------------------------------
 *   /certificates/<uuid>        a COURSE certificate    GeneratedCertificate, upstream's table
 *   /certificates/path/<uuid>   a PATHWAY certificate   PathwayCertificate, ours
 *
 * They are different rows in different tables written by different triggers, and this file
 * deliberately does not care: `certificate-view.ts` normalises both into one
 * `CertificateDocument`, and the only visible difference is that a pathway certificate
 * lists the courses that earned it. Branching further down would let the two drift, and a
 * learner holding both would notice immediately.
 *
 * PUBLIC. No `RequirePersona` around either route, and the data comes from endpoints that
 * take no credentials — a certificate whose link only works for its owner is not a
 * credential anyone can check. Everything shown here is therefore deliberately public: the
 * recipient's name, what they completed, the issue date and the credential id, which is
 * exactly what the platform's own page shows and what is printed on a paper certificate.
 *
 * NOINDEX. Public and shareable is not the same as "a search result for this person's
 * name". The API sends `X-Robots-Tag` and this page sets the meta equivalent, because a
 * crawler that renders the app never sees the header.
 *
 * Nothing here reads the session, so signed-in and signed-out visitors see the same page.
 */

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Banner, EmptyState, Icon, Spinner } from '../../shared/ui';
import { getConfig } from '../../shared/lib/config';
import { ApiError } from '../../shared/lib/openedx';
import {
  courseDocument,
  credentialId,
  formatCertificateDate,
  getIssuedCertificate,
  getIssuedPathwayCertificate,
  linkedInShareUrl,
  pathwayDocument,
  type CertificateDocument,
} from '../../shared/lib/certificate-view';
import './certificate-view.css';

/**
 * Keep this page out of search results, and put it back on the way out.
 *
 * A `<meta name="robots">` written into the head, because this is a single-page app: the
 * document served for every route is the same `index.html`, so the tag cannot be static
 * without applying to the whole academy. Removed on unmount so navigating away from a
 * certificate does not silently de-index the catalogue.
 */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, noarchive';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
}

export default function CertificateView({ kind = 'course' }: { kind?: 'course' | 'pathway' }) {
  const { uuid = '' } = useParams();
  const [params] = useSearchParams();
  useNoIndex();

  const certificate = useQuery({
    // The kind is part of the key: the two namespaces are independent, and the same 32 hex
    // characters could in principle exist in both.
    queryKey: ['issued-certificate', kind, uuid],
    queryFn: async (): Promise<CertificateDocument> =>
      kind === 'pathway'
        ? pathwayDocument(await getIssuedPathwayCertificate(uuid))
        : courseDocument(await getIssuedCertificate(uuid)),
    enabled: Boolean(uuid),
    // A certificate does not change. Refetching one on every focus is pure noise, and this
    // page is frequently left open in a tab.
    staleTime: 60 * 60_000,
    retry: false,
  });

  if (certificate.isLoading) {
    return (
      <main className="cert">
        <Spinner label="Loading this certificate…" />
      </main>
    );
  }

  // One message for every refusal, because the endpoints answer 404 for all of them on
  // purpose — "held back", "revoked", "another academy's" and "never existed" are not
  // distinguished to a visitor, so the page must not pretend to know which it was.
  if (certificate.isError || !certificate.data) {
    const status = certificate.error instanceof ApiError ? certificate.error.status : null;
    return (
      <main className="cert">
        <EmptyState
          title="This certificate is not available"
          body={
            status === 404
              ? 'The link may be mistyped, or the certificate may not have been published by the academy. Check the link with whoever shared it.'
              : 'Something went wrong loading this certificate. Refresh the page to try again.'
          }
        />
      </main>
    );
  }

  return <Certificate document={certificate.data} autoPrint={params.get('print') === '1'} />;
}

function Certificate({
  document: doc,
  autoPrint,
}: {
  document: CertificateDocument;
  /**
   * Arrived here from a "Download PDF" button somewhere else in the app.
   *
   * That button has to DO something — a download control that does nothing is the exact
   * defect the prototype ships — and the only download this product has is the browser's
   * own print-to-PDF, which can only be raised from the page holding the document. So the
   * button navigates here with `?print=1` and this opens the dialog once the certificate
   * has actually rendered. Printing before that would produce a page of loading text.
   */
  autoPrint: boolean;
}) {
  // The academy whose host this is, from the tenant config — which is also where the logo,
  // the palette and the typography on this page come from. That is the whole of "tenant
  // branding": the page is host-branded already, so a certificate opened on an academy's
  // own host is in that academy's brand with nothing added.
  //
  // `issuerName` wins when the credential recorded one. A pathway certificate snapshots its
  // issuer at award time, so an academy that renames itself — or a licensee whose licence
  // later ends — cannot change who a document already in someone's hands says issued it.
  const live = getConfig().tenant.name;
  const academy = doc.issuerName || live;
  const issued = formatCertificateDate(doc.issued);
  const [copied, setCopied] = useState(false);

  // The certificate's own address, and the thing a visitor is asked to trust. Read from the
  // browser rather than rebuilt from the uuid so a share always carries the URL the visitor
  // is actually looking at, including its host.
  //
  // WITHOUT THE QUERY STRING. A learner who arrives via "Download PDF" is on `?print=1`, and
  // copying or sharing that would hand somebody a link that opens their print dialog. The
  // credential is the path; the query is how this tab got here.
  const pageUrl =
    typeof window === 'undefined' ? '' : `${window.location.origin}${window.location.pathname}`;

  useEffect(() => {
    window.document.title = `${doc.title} — certificate | ${academy}`;
  }, [doc.title, academy]);

  useEffect(() => {
    if (!autoPrint) return;
    // One frame after paint, so the seal, the fonts and the course list are in the output.
    // `window.print()` is synchronous and blocking, which is what makes a single fire safe:
    // the flag comes from the URL, so a reload legitimately prints again and nothing loops.
    const timer = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(timer);
  }, [autoPrint, doc.uuid]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
    } catch {
      // Clipboard access is refused outside a secure context and in some embedded browsers.
      // Selecting the link is the fallback that always works, so it is offered rather than
      // reporting a failure the visitor can do nothing about.
      setCopied(false);
      window.prompt('Copy this link', pageUrl);
    }
  };

  return (
    <main className="cert">
      {/*
        Says what the page is, for a visitor who did not earn this and may never have heard
        of the academy. NOT the prototype's "Your certificate is ready" — this link is opened
        by employers and registrars far more often than by its owner, and addressing them as
        the holder is simply untrue.
      */}
      <div className="cert__head">
        <h1>{doc.kind === 'pathway' ? 'Verified learning path certificate' : 'Verified certificate'}</h1>
        <p>
          Issued by {academy}
          {issued ? ` · ${issued}` : ''}
        </p>
      </div>

      {/*
        `aria-label` rather than a heading: the card is a document, and its own text is laid
        out for the eye (an eyebrow, then the title, then the name) in an order that reads
        as a certificate rather than as a document outline.
      */}
      <article className="cert-card" aria-label="Certificate">
        <div className="cert-card__seal" aria-hidden="true">
          <Icon name="workspace_premium" />
        </div>

        <div className="cert-card__eyebrow">
          {academy}
          {doc.label ? ` · ${doc.label}` : ''}
        </div>

        <h2 className="cert-card__title">{doc.title}</h2>

        <p className="cert-card__label">This certificate is awarded to</p>
        <p className="cert-card__recipient">{doc.recipient.name}</p>

        {doc.subject && (
          <>
            <p className="cert-card__label">for successfully completing</p>
            <p className="cert-card__course">{doc.subject}</p>
          </>
        )}

        {doc.description && <p className="cert-card__description">{doc.description}</p>}

        {/*
          THE COURSES THAT EARNED IT — the one thing a pathway certificate has to say that a
          course certificate does not. Rendered only when the payload carries a list, so this
          block is simply absent on a course certificate rather than being an empty section.

          NAMES AND DATES, NOT LINKS. A step course whose `certificates_display_behavior` is
          "end" with no end date issues a certificate whose own page 404s — so linking each
          row would put broken links on a credential. The dates are the course certificates'
          own issue dates, snapshotted when this one was written.
        */}
        {doc.courses.length > 0 && (
          <section className="cert-card__courses" aria-label="Courses completed">
            <h3>
              Completed as part of this learning path
              <span>
                {doc.courses.length} {doc.courses.length === 1 ? 'course' : 'courses'}
              </span>
            </h3>
            <ol>
              {doc.courses.map((course) => (
                <li key={`${course.order}-${course.title}`}>
                  <span className="cert-card__course-index" aria-hidden="true">
                    {course.order}
                  </span>
                  <span className="cert-card__course-title">{course.title}</span>
                  <span className="cert-card__course-date">
                    {formatCertificateDate(course.completed_on) || '—'}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}

        <div className="cert-card__foot">
          {/*
            Signatories are optional and usually absent: a course awards a certificate here
            whether or not anybody has configured a signature line, and a pathway has no
            signatory authoring at all. When there are none the issuing academy signs it,
            which is true and is better than an empty ruled line that looks like a missing
            name. No person's name and no signature image is ever invented.
          */}
          {doc.signatories.length > 0 ? (
            doc.signatories.map((signatory, index) => (
              <div className="cert-card__col" key={`${signatory.name}-${index}`}>
                {signatory.image_url && (
                  <img
                    className="cert-card__signature"
                    src={signatory.image_url}
                    alt=""
                    /* Decorative: the name below it is the information. */
                  />
                )}
                <div className="cert-card__signame">{signatory.name}</div>
                <div className="cert-card__sigrole">
                  {[signatory.title, signatory.organization].filter(Boolean).join(', ')}
                </div>
              </div>
            ))
          ) : (
            <div className="cert-card__col">
              {/*
                The ACADEMY's name, not the organisation's, even though the org is the
                certifying body in the platform's model. They are usually near-identical
                strings and the learner only ever knew the academy — printing the org's
                database name beside the academy's own name in the eyebrow reads as two
                different issuers for one certificate.
              */}
              <div className="cert-card__signame">{academy || doc.orgName}</div>
              <div className="cert-card__sigrole">Issuing academy</div>
            </div>
          )}

          <div className="cert-card__col">
            <div className="cert-card__signame">{issued || 'Issue date unavailable'}</div>
            <div className="cert-card__sigrole">
              Credential ID · <span className="cert-card__id">{credentialId(doc.uuid)}</span>
            </div>
          </div>
        </div>
      </article>

      {/* Hidden when printing: they are controls, not part of the document. */}
      <div className="cert__actions">
        <button className="ui-btn" data-variant="primary" onClick={() => window.print()}>
          <Icon name="download" /> Download PDF
        </button>
        <a
          className="ui-btn"
          data-variant="secondary"
          href={linkedInShareUrl(pageUrl)}
          target="_blank"
          rel="noreferrer noopener"
        >
          <Icon name="share" /> Share to LinkedIn
        </a>
        <button className="ui-btn" data-variant="ghost" onClick={copyLink}>
          <Icon name="link" /> Copy verify link
        </button>
        {/* The button's own label changing would be silent to a screen reader; a live region
            is what announces it. Empty until there is something to say, so it is not read
            out on load. */}
        <span className="cert__copied" role="status" aria-live="polite">
          {copied ? 'Link copied to the clipboard' : ''}
        </span>
      </div>

      {/* Says what the page IS, which is the question a stranger following this link has. */}
      <Banner tone="info">
        Anyone with this link can check this certificate. It was issued by {academy} to{' '}
        {doc.recipient.name} and its credential id is {credentialId(doc.uuid)}.
      </Banner>
    </main>
  );
}
